import { useCallback, useEffect, useRef } from "react";
import { useCalculatorStore } from "./useCalculatorStore";
import {
  calculateCargoPriceApi,
  fetchInitialQueriesAndFleet,
  updateQueryDetailsApi,
  updateFleetDataApi,
  addCargoExtraDetailsApi,
  deleteCargoExtraDetailApi,
  deleteFleetApi,
  updateChangeSwitchQueryApi,
  fetchPortDistanceMatrix,
  resetPortDistanceMatrix,
} from "../services/calculatorApi";
import { allFleetCalculator } from "../../actions";
import { placeAddedOperation } from "../../dataMapping";
import {
  addPortDistanceToOperations,
  addPortDistanceToSelectedFleet,
  convertPortOperationToDistanceString,
  fleetWithCalculatorDataMapping,
  getTermFactor,
} from "../utils/dataMapping";
import { safeArray, safeJsonParse, safeNumber } from "../utils/validations";
import { initialCreatePortOperation } from "../../../../../constants";
import { toastError, toastSuccess } from "../../../../../utils";
import { v4 as uuidv4 } from "uuid";

/**
 * Constructs the exact payload signature expected by POST /api/clientQuery/calculate/cargoPrice
 */
export const buildCalculatorPayload = (currentState) => {
  const portOps = safeArray(currentState.portOperation);
  const fleets = safeArray(currentState.fleetList);
  const query = currentState.selectedQuery || {};

  const cargoList = portOps.filter((item) => !item.isExtra);
  const cargoExtraDetails = portOps.filter((item) => Boolean(item.isExtra));

  const updatedCargoList = cargoList.map((cargoDetails) => {
    const { _id, geoJSON, ...restCargoMainDetails } = cargoDetails;
    const { _id: cargoDetailId, ...restCargoDetail } = cargoDetails?.cargoDetails || {};
    return {
      ...restCargoMainDetails,
      sulfur: cargoDetails.sulfur || "Normal",
      loadline: cargoDetails.loadline || "S-Summer",
      cargoDetails: { ...restCargoDetail },
    };
  });

  const {
    _id,
    cargoList: clientCargoList,
    createdUser,
    lastModifiedUser,
    userId,
    calculationFormat,
    __v,
    createdAt,
    updatedAt,
    isCalculate,
    ...restClientQuery
  } = query;

  const updatedClientQuery = {
    ...restClientQuery,
    cargoList: updatedCargoList,
    calculationFormat: portOps.map((item) => item._id).filter(Boolean).join(","),
  };

  // Generate fresh UUIDs for every fleet entry (matching old module behavior)
  const fleetsWithNewIds = fleets.map((f) => ({ ...f, vesselUniqueId: uuidv4() }));

  const updatedFleetListData = fleetsWithNewIds.map((fleetDetails) => {
    const vesselDetails = safeJsonParse(fleetDetails?.vessel, {});
    const {
      _id: parseVesselId,
      userId: parseVesselUserId,
      createdUser: parseVesselCreatedUser,
      lastModifiedUser: parseVesselModifiedUser,
      vesselExtraDetails,
      __v: parseVessel__v,
      createdAt,
      createdDate,
      lastModifiedDate,
      pattern,
      updatedAt,
      vesselLabel,
      vesselAHL,
      vesselCO2,
      vesselCountry,
      vesselGrabs,
      vesselIceClass,
      vesselIMO,
      vesselLdnConsmMGO1,
      vesselLdnConsmMGO2,
      vesselLightShip,
      vesselMMSI,
      vesselType,
      vesselSource,
      vesselName,
      ...parseVesselDetails
    } = vesselDetails;

    const {
      _id,
      vesselId,
      createdUser: fleetCreatedUser,
      organizationId,
      userId: fleetUserId,
      lastModifiedUser: fleetModUser,
      vesselUniqueId,
      __v: fleet__v,
      ...restFleet
    } = fleetDetails;

    const uniqueId = vesselUniqueId;

    return {
      ...restFleet,
      vessel: JSON.stringify({
        id: uniqueId,
        ...parseVesselDetails,
        system: true,
      }),
      vesselId: uniqueId,
    };
  });

  const updatedCargoExtraDetails = cargoExtraDetails.map((cargoextraDetails) => {
    const { _id, geoJSON, ...restCargoMainDetails } = cargoextraDetails;
    const { _id: cargoDetailId, ...restCargoDetail } = cargoextraDetails?.cargoDetails || {};
    return {
      ...restCargoMainDetails,
      sulfur: cargoextraDetails.sulfur || "Normal",
      loadline: cargoextraDetails.loadline || "S-Summer",
      cargoDetails: { ...restCargoDetail },
    };
  });

  return {
    autoSave: false,
    cargoExtraDetails: updatedCargoExtraDetails,
    cargoList: updatedCargoList,
    clientQuery: updatedClientQuery,
    fleetList: updatedFleetListData,
  };
};

/**
 * Synchronizes selected vessel opening port, delivery bunker prices, and per-port calculation result map into portOperation
 */
export const syncVesselWithPortOperations = (fleet = {}, portOps = [], fuelType = "TYPE0") => {
  const safeOps = safeArray(portOps).map((op) => ({ ...op }));
  if (!safeOps.length) return safeOps;

  const vesselPort = fleet.port || safeOps[0]?.port || "";
  const vesselObj = safeJsonParse(fleet.vessel, {});
  const currentType = (vesselObj.fuelType || fuelType || "TYPE0").replace("TYPE", "");
  const portToPortOperationResultMap = fleet.portToPortOperationResultMap || [];

  const {
    bnkDelhs = 0,
    bnkDells = 0,
    bnkDelmgo = 0,
    bnkPriceDeliveryhs = 0,
    bnkPriceDeliveryls = 0,
    bnkPriceDeliverymgo = 0,
  } = fleet;

  return safeOps.map((operation, index) => {
    const selectedResultMap = portToPortOperationResultMap[index] || {};
    const { distanceECA, distanceNormal, ...restResultMap } = selectedResultMap;

    let commencePort = operation.port || "";
    if (index === 0) {
      commencePort = vesselPort;
    }

    let cargoDetails = { ...(operation.cargoDetails || {}) };
    if (index === 0) {
      cargoDetails = {
        ...cargoDetails,
        bunkerSuppliedHSFO: bnkDelhs,
        bunkerSuppliedLSFO: bnkDells,
        bunkerSuppliedMGO: bnkDelmgo,
        [`fuelPriceType${currentType}Hsfo`]: bnkPriceDeliveryhs,
        [`fuelPriceType${currentType}Lsfo`]: bnkPriceDeliveryls,
        [`fuelPriceType${currentType}Mgo`]: bnkPriceDeliverymgo,
      };
    }

    return {
      ...operation,
      ...restResultMap,
      port: commencePort,
      cargoDetails,
    };
  });
};

export const useCalculatorController = ({ setLoader = () => { } } = {}) => {
  const store = useCalculatorStore();
  const {
    state,
    setInitialData,
    setSelectedQuery,
    setFleetList,
    setSelectedVessel,
    setPortOperations,
    updatePortOperation,
    addPortOperation,
    deletePortOperation,
    setCalculationResult,
    setLoading: storeSetLoading,
    setError,
  } = store;

  // Bridge controller loading state to global app loader overlay
  const setLoading = useCallback(
    (loading, message = "") => {
      storeSetLoading(loading, message);
      setLoader(loading, message);
    },
    [storeSetLoading, setLoader]
  );

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);



  /**
   * Executes cargo price calculation and handles autoSave database persist
   */
  const runCalculation = useCallback(
    async (autoSave = false, overrideState = null) => {
      const currentState = overrideState || stateRef.current;
      const portOps = safeArray(currentState.portOperation);
      const fleets = safeArray(currentState.fleetList);
      const query = currentState.selectedQuery || {};

      if (!portOps.length || !fleets.length || !query._id) {
        return;
      }

      setLoading(true, "Calculating pricing and results...");

      const payload = buildCalculatorPayload(currentState);

      // Build a fleet list with the same vesselUniqueId as the payload sent
      // so that fleetWithCalculatorDataMapping can match API response keys correctly
      const fleetsWithPayloadIds = fleets.map((f, i) => {
        const payloadFleet = payload.fleetList[i] || {};
        return { ...f, vesselUniqueId: payloadFleet.vesselId || f.vesselUniqueId };
      });

      try {
        const response = await calculateCargoPriceApi(payload);
        if (response && response.data) {
          const updatedFleets = fleetWithCalculatorDataMapping(fleetsWithPayloadIds, response.data);
          setFleetList(updatedFleets);

          const selectedId = currentState.selectedVessel?._id;
          const matchedSelected = selectedId
            ? updatedFleets.find((f) => f._id === selectedId) || updatedFleets[0]
            : updatedFleets[0];

          if (matchedSelected) {
            setSelectedVessel(matchedSelected);
            const syncedOps = syncVesselWithPortOperations(
              matchedSelected,
              portOps,
              query.fuelType
            );
            setPortOperations(syncedOps);
          }
          setCalculationResult(response.data || {});

          // Database Persist when user clicks "Calculate and Save"
          if (autoSave) {
            const {
              __v, version, system, createdUser, vesselId, vesselName, userId,
              lastModifiedUser, lastModifiedDate, ...restClientQuery
            } = query;

            const newUpdatedQuery = {
              ...restClientQuery,
              cargoList: portOps,
              createdUser: typeof createdUser === "object" ? createdUser?._id : createdUser,
            };

            const newUpdatedFleet = { ...(matchedSelected || currentState.selectedVessel) };

            setLoading(true, "Saving calculation to database...");
            const res = await Promise.all([
              updateQueryDetailsApi(newUpdatedQuery),
              updateFleetDataApi(newUpdatedFleet),
            ]);

            if (res?.[0]?.success && res?.[1]?.success) {
              toastSuccess("Calculation updated and saved successfully!");
              if (res[0].data) {
                setSelectedQuery(res[0].data);
              }
            }
          }
        }
      } catch (err) {
        toastError(err?.data?.message || err?.message || "Calculation failed.");
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setFleetList, setSelectedVessel, setCalculationResult]
  );

  /**
   * Field edit handler for Port Operations
   */
  const handlePortOperationChange = useCallback(
    (index, e, cargoUpdate = false) => {
      const targetObj = e?.target ? e.target : (e || {});
      const name = targetObj.name || "";
      const value = targetObj.value !== undefined ? targetObj.value : "";
      if (name === "terms") {
        const factor = getTermFactor(value);
        updatePortOperation({ index, name: "terms", value, cargoUpdate: false });
        updatePortOperation({ index, name: "factor", value: factor, cargoUpdate: false });
      } else {
        updatePortOperation({ index, name, value, cargoUpdate });
      }
    },
    [updatePortOperation]
  );

  /**
   * Field edit handler for Selected Vessel inputs (supports event, (id, name, value), or object signatures)
   */
  const handleSelectedVesselChange = useCallback(
    (arg1, arg2, arg3) => {
      const currentState = stateRef.current;
      const currentFleet = { ...currentState.selectedVessel };
      const vesselObj = safeJsonParse(currentFleet.vessel, {});

      let name = "";
      let value = "";
      let isFleetEdit = false;

      if (typeof arg1 === "string" && typeof arg2 === "string") {
        // Called as (id, name, value)
        name = arg2;
        value = arg3;
        isFleetEdit = true;
      } else if (arg1 && arg1.target) {
        // Called as React event (e, isFleetEdit)
        const { name: targetName = "", value: targetValue = "", type = "", checked = false } = arg1.target;
        name = targetName;
        value = type === "checkbox" ? checked : targetValue;
        isFleetEdit = Boolean(arg2);
      } else if (arg1 && typeof arg1 === "object") {
        name = arg1.name || "";
        value = arg1.value !== undefined ? arg1.value : "";
        isFleetEdit = Boolean(arg2);
      }

      if (!name) return;

      let updatedFleet = { ...currentFleet };

      if (isFleetEdit) {
        updatedFleet[name] = value;
      } else {
        vesselObj[name] = value;
        updatedFleet.vessel = JSON.stringify(vesselObj);
      }

      setSelectedVessel(updatedFleet);

      const updatedFleets = currentState.fleetList.map((item) =>
        item._id === updatedFleet._id ? updatedFleet : item
      );
      setFleetList(updatedFleets);

      if (
        name === "bnkPriceDeliveryhs" ||
        name === "bnkPriceDeliveryls" ||
        name === "bnkPriceDeliverymgo" ||
        name === "bnkDelhs" ||
        name === "bnkDells" ||
        name === "bnkDelmgo"
      ) {
        const portOps = [...currentState.portOperation];
        if (portOps.length > 0) {
          const firstPort = { ...portOps[0] };
          const cargoDetails = { ...(firstPort.cargoDetails || {}) };
          const fuelType = currentState.selectedQuery?.fuelType || "TYPE0";
          const currentType = fuelType.replace("TYPE", "");

          if (name === "bnkPriceDeliveryhs") cargoDetails[`fuelPriceType${currentType}Hsfo`] = value;
          if (name === "bnkPriceDeliveryls") cargoDetails[`fuelPriceType${currentType}Lsfo`] = value;
          if (name === "bnkPriceDeliverymgo") cargoDetails[`fuelPriceType${currentType}Mgo`] = value;
          if (name === "bnkDelhs") cargoDetails.bunkerSuppliedHSFO = value;
          if (name === "bnkDells") cargoDetails.bunkerSuppliedLSFO = value;
          if (name === "bnkDelmgo") cargoDetails.bunkerSuppliedMGO = value;

          firstPort.cargoDetails = cargoDetails;
          portOps[0] = firstPort;
          setPortOperations(portOps);
        }
      }
    },
    [setSelectedVessel, setFleetList, setPortOperations]
  );

  /**
   * Handler to update calculator data object from child components
   */
  const handleSetCalculatorData = useCallback(
    (newCalcData = {}) => {
      const currentState = {
        clientQuery: stateRef.current.selectedQuery,
        portOperation: stateRef.current.portOperation,
        fleetList: stateRef.current.fleetList,
        selectedVessel: stateRef.current.selectedVessel,
      };

      const calcData = typeof newCalcData === "function" ? newCalcData(currentState) : newCalcData;
      let queryToSet = calcData.clientQuery;

      if (calcData.portOperation) {
        setPortOperations(calcData.portOperation);

        if (queryToSet) {
          queryToSet = { ...queryToSet, cargoList: calcData.portOperation };
        }
      }

      if (calcData.fleetList) {
        setFleetList(calcData.fleetList);
      }

      if (queryToSet) {
        setSelectedQuery(queryToSet);
      }
    },
    [setPortOperations, setFleetList, setSelectedQuery]
  );

  /**
   * Updates canal avoidance options in selectedQuery
   */
  const handlePortCanalChange = useCallback(
    (canalState = {}) => {
      const currentState = stateRef.current;
      const updatedQuery = {
        ...currentState.selectedQuery,
        ...canalState,
      };
      setSelectedQuery(updatedQuery);
    },
    [setSelectedQuery]
  );

  /**
   * Fetches port-to-port distance matrix for reordered or reset port operations
   */
  const handleFetchPortDistance = useCallback(
    async (reset = false, propCalculator = null, portCanalChecks = {}, isSave = false) => {
      let currentState = { ...stateRef.current };
      if (propCalculator) {
        currentState = {
          ...currentState,
          ...propCalculator,
          portOperation: propCalculator.portOperation || currentState.portOperation,
          clientQuery: propCalculator.clientQuery || propCalculator.selectedQuery || currentState.selectedQuery,
        };
      }

      const query = currentState.selectedQuery || {};
      const { getPortDistanceString, routeList } = convertPortOperationToDistanceString(currentState);
      const queryId = currentState.clientQuery?._id || query?._id;

      const portDataObj = {
        eca: query.eca || false,
        hra: query.hra || false,
        jwc: query.jwc || false,
        suez: query.suez || false,
        panama: query.panama || false,
        singapore: query.singapore || false,
        routeOverride: false,
        getGeometry: false,
        ...portCanalChecks,
        routeList,
        portList: getPortDistanceString,
        clientQueryId: queryId,
        queryId,
      };

      setLoading(true, reset ? "Resetting distances to system values..." : "Recalculating distances...");
      try {
        const res = reset
          ? await resetPortDistanceMatrix(portDataObj)
          : await fetchPortDistanceMatrix(portDataObj);

        if (res && res.data) {
          const realData = reset ? JSON.parse(JSON.stringify(res.data)) : (res.realData || res.data);
          const updatedPortOps = addPortDistanceToOperations(
            res.data,
            [...currentState.portOperation],
            realData,
            true, // addGeometry
            reset // reset flag
          );

          setPortOperations(updatedPortOps);

          const updatedFleet = addPortDistanceToSelectedFleet(updatedPortOps, currentState.selectedVessel);
          if (updatedFleet && updatedFleet._id) {
            setSelectedVessel(updatedFleet);
          }

          const updatedQuery = {
            ...currentState.selectedQuery,
            ...portCanalChecks,
            cargoList: updatedPortOps,
            portToPortDistanceReal: {
              payload: res.payload || portDataObj,
              output: realData,
            },
            portToPortDistance: {
              payload: res.payload || portDataObj,
              output: res.data,
            },
          };

          // Always update selectedQuery in reducer store so map routes refresh immediately
          setSelectedQuery(updatedQuery);

          const updatedState = {
            ...currentState,
            portOperation: updatedPortOps,
            selectedQuery: updatedQuery,
            selectedVessel: updatedFleet || currentState.selectedVessel,
          };

          await runCalculation(isSave, updatedState);
        }
      } catch (err) {
        toastError(err?.data?.message || err?.message || "Failed to calculate distance.");
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setPortOperations, setSelectedVessel, setSelectedQuery, runCalculation]
  );

  /**
   * Main initial data loading function
   */
  const loadInitialData = useCallback(
    async (offset = 1) => {
      setLoading(true, "Fetching query and fleet initial data...");
      try {
        const data = await fetchInitialQueriesAndFleet({ offset, perPage: 50 });
        const selectedQuery = data.queries?.[0] || {};
        const fleetList = data.fleets || [];
        const selectedVessel = fleetList[0] || {};

        const syncedOps = syncVesselWithPortOperations(
          selectedVessel,
          selectedQuery?.cargoList || [],
          selectedQuery?.fuelType
        );

        const updatedState = {
          allQueries: data.queries || [],
          fleetList,
          selectedQuery,
          selectedVessel,
          portOperation: syncedOps,
          pagination: {
            offset: data.currentPage || offset,
            totalItems: data.totalItems || 0,
            totalPages: data.totalPages || 1,
          },
        };

        setInitialData(updatedState);

        if (fleetList.length > 0 && selectedQuery._id) {
          handleFetchPortDistance(false, updatedState);
        }
      } catch (err) {
        setError(err?.data?.message || err?.message || "Failed to load initial data.");
        toastError(err?.data?.message || "Failed to load calculator data.");
      } finally {
        setLoading(false);
      }
    },
    [setInitialData, setLoading, setError, handleFetchPortDistance]
  );

  /**
   * Adds a new port operation via database API at addIndex
   */
  const handleAddOperation = useCallback(
    async (requiredData = {}, addIndex = 0) => {
      const currentState = stateRef.current;
      const queryId = currentState.selectedQuery?._id;
      if (!queryId) return;

      setLoading(true, "Adding new operation...");
      try {
        const res = await addCargoExtraDetailsApi({
          details: {
            ...initialCreatePortOperation,
            ...requiredData,
          },
          queryId,
          addIndex,
        });

        if (res?.data?.cargoList) {
          const newCargoList = placeAddedOperation(
            safeArray(currentState.portOperation),
            res.data.cargoList,
            addIndex
          );

          const updatedQuery = {
            ...currentState.selectedQuery,
            cargoList: newCargoList,
            calculationFormat: res.data.calculationFormat,
          };

          // We must update distances for the newly inserted port
          handleFetchPortDistance(false, {
            ...currentState,
            selectedQuery: updatedQuery,
            portOperation: newCargoList,
          }, {}, true);
        }
      } catch (err) {
        toastError(err?.data?.message || err?.message || "Failed to add operation.");
        setLoading(false);
      }
    },
    [setLoading, handleFetchPortDistance]
  );

  /**
   * Deletes a port operation via database API
   */
  const handleDeleteOperation = useCallback(
    async (index, id) => {
      const currentState = stateRef.current;
      const queryId = currentState.selectedQuery?._id;
      if (!queryId) return;

      const portOps = [...currentState.portOperation];
      const targetOp = portOps[index] || {};
      const cargoId = id || targetOp._id || targetOp.id || targetOp.cargoId;

      if (!cargoId || cargoId === "null" || cargoId === "undefined") {
        // If operation has no persistent _id yet, simply remove from local state
        if (index >= 0 && index < portOps.length) {
          portOps.splice(index, 1);
        }
        const mappedPortOps = portOps.map((item, idx) => ({ ...item, index: idx }));
        const updatedQuery = {
          ...currentState.selectedQuery,
          cargoList: mappedPortOps,
        };
        setSelectedQuery(updatedQuery);
        setPortOperations(mappedPortOps);
        handleFetchPortDistance(false, {
          ...currentState,
          selectedQuery: updatedQuery,
          portOperation: mappedPortOps,
        }, {}, true);
        return;
      }

      setLoading(true, "Deleting operation...");
      try {
        const res = await deleteCargoExtraDetailApi({ queryId, cargoId });
        if (index >= 0 && index < portOps.length) {
          portOps.splice(index, 1);
        }

        // Re-map indexes properly
        const mappedPortOps = portOps.map((item, idx) => ({ ...item, index: idx }));

        const updatedQuery = {
          ...currentState.selectedQuery,
          calculationFormat: res?.data?.calculationFormat || "",
          cargoList: mappedPortOps,
        };

        setSelectedQuery(updatedQuery);
        setPortOperations(mappedPortOps);

        // Recalculate distances for remaining ports since a middle port may have been removed
        handleFetchPortDistance(false, {
          ...currentState,
          selectedQuery: updatedQuery,
          portOperation: mappedPortOps,
        }, {}, true);
      } catch (err) {
        toastError(err?.data?.message || err?.message || "Failed to delete operation.");
        setLoading(false);
      }
    },
    [setLoading, setPortOperations, setSelectedQuery, handleFetchPortDistance]
  );

  /**
   * Deletes a vessel / fleet row via database API
   */
  const handleDeleteFleet = useCallback(
    async (fleetId) => {
      const currentState = stateRef.current;
      setLoading(true, "Deleting fleet...");
      try {
        await deleteFleetApi(fleetId);
        toastSuccess("Vessel deleted successfully");
        const res = await allFleetCalculator({
          offset: currentState.pagination.offset || 1,
          perPage: currentState.pagination.limit || 50,
        });

        if (Array.isArray(res?.data?.list)) {
          setFleetList(res.data.list);
          runCalculation();
        }
      } catch (err) {
        toastError(err?.data?.message || err?.message || "Failed to delete vessel.");
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setFleetList, runCalculation]
  );

  /**
   * Removes order query from calculator view via database API
   */
  const handleRemoveOrderQuery = useCallback(
    async (id) => {
      setLoading(true, "Removing query...");
      try {
        await updateChangeSwitchQueryApi({ id, isShowInCalculator: false });
        await loadInitialData(1);
      } catch (err) {
        toastError(err?.data?.message || err?.message || "Failed to remove query.");
      } finally {
        setLoading(false);
      }
    },
    [setLoading, loadInitialData]
  );

  /**
   * Handles user selecting a different query
   */
  const handleQuerySelect = useCallback(
    (query) => {
      setSelectedQuery(query);

      const currentState = stateRef.current;
      const activeFleet = currentState.selectedVessel || currentState.fleetList?.[0] || {};
      const syncedOps = syncVesselWithPortOperations(
        activeFleet,
        query?.cargoList,
        query?.fuelType
      );

      setPortOperations(syncedOps);

      handleFetchPortDistance(false, {
        ...currentState,
        selectedQuery: query,
        portOperation: syncedOps,
      });
    },
    [setSelectedQuery, setPortOperations, handleFetchPortDistance]
  );

  /**
   * Handles user selecting a fleet row
   */
  const handleFleetSelect = useCallback(
    (fleetRow) => {
      setSelectedVessel(fleetRow);

      const currentState = stateRef.current;
      const syncedOps = syncVesselWithPortOperations(
        fleetRow,
        currentState.portOperation,
        currentState.selectedQuery?.fuelType
      );

      setPortOperations(syncedOps);
    },
    [setSelectedVessel, setPortOperations]
  );

  /**
   * Handles reordering of port operations
   */
  const handleReorderOperation = useCallback(
    async (oldIndex, newIndex) => {
      const currentState = stateRef.current;
      const previousPortOps = [...currentState.portOperation];
      const previousQuery = { ...currentState.selectedQuery };

      // 1. Optimistically update store state so UI port list reflects new order immediately (no flicker)
      const portOps = [...currentState.portOperation];
      const [movedItem] = portOps.splice(oldIndex, 1);
      portOps.splice(newIndex, 0, movedItem);
      const mappedPortOps = portOps.map((item, idx) => ({ ...item, index: idx }));

      const updatedQuery = {
        ...currentState.selectedQuery,
        cargoList: mappedPortOps,
      };

      setPortOperations(mappedPortOps);
      setSelectedQuery(updatedQuery);

      // 2. Recalculate distances for the new order and save (standard loader runs as before)
      try {
        await handleFetchPortDistance(
          false,
          {
            ...currentState,
            selectedQuery: updatedQuery,
            portOperation: mappedPortOps,
          },
          {},
          true
        );
      } catch (err) {
        // Rollback on failure
        setPortOperations(previousPortOps);
        setSelectedQuery(previousQuery);
        toastError(err?.data?.message || err?.message || "Failed to reorder ports.");
      }
    },
    [handleFetchPortDistance, setPortOperations, setSelectedQuery]
  );

  /**
   * Handles inline port updates (e.g. changing location from dropdown)
   */
  const handlePortChange = useCallback(
    (portIndex, selectedPort = {}) => {
      const currentState = stateRef.current;
      const portOps = [...currentState.portOperation];

      if (portIndex > -1 && portOps[portIndex]) {
        const portName = selectedPort.value || selectedPort.name || selectedPort.port || "";
        portOps[portIndex] = {
          ...portOps[portIndex],
          port: portName,
          lat: selectedPort.lat || 0,
          lng: selectedPort.lng || 0,
        };

        const updatedQuery = {
          ...currentState.selectedQuery,
          cargoList: portOps,
        };

        // Recalculate distances instantly and save
        handleFetchPortDistance(false, {
          ...currentState,
          selectedQuery: updatedQuery,
          portOperation: portOps,
        }, {}, true);
      }
    },
    [handleFetchPortDistance]
  );

  return {
    state,
    loadInitialData,
    runCalculation,
    handlePortOperationChange,
    handleSelectedVesselChange,
    handleSetCalculatorData,
    handleAddOperation,
    handleDeleteOperation,
    handleDeleteFleet,
    handleRemoveOrderQuery,
    handleFetchPortDistance,
    handlePortCanalChange,
    handleReorderOperation,
    handlePortChange,
    handleQuerySelect,
    handleFleetSelect,
    setIsOverride: store.setIsOverride,
  };
};
